<template>
  <div v-if="eventData.type === 'relation'" class="relation" :class="{ 'related-item': eventData.isRelatedItem }">
    <div class="full-width flex items-start">
      <div class="flex items-center event-name q-mr-lg">
        <q-icon name="engp:engp-ico-relation" size="13px" class="q-mr-sm"></q-icon>
        <b>Relation</b>
      </div>
      <div class="event-message">
        {{ eventData.isRelatedItem ? 'Workflow' : normilizeItem(eventData.itemType) }}
        <template v-if="eventData.isRelatedItem">
          was {{ eventData.description }} by {{ normilizeItem(eventData.itemType) }}
          <span
            class="cursor-pointer text-blue"
            @click.stop="goToItem($event, { itemType: 'workflows', itemId: eventData.workflowUuid })"
            >{{ eventData.workflowUuid }}
            <q-tooltip>Go to item</q-tooltip>
          </span>
        </template>
        <template v-else>
          <span
            v-if="normilizeItem(eventData.itemType) !== 'Registration' && normilizeItem(eventData.itemType) !== 'Item'"
            class="cursor-pointer text-blue"
            @click.stop="goToItem($event, eventData)"
            >{{ eventData.itemId }}
            <q-tooltip>Go to item</q-tooltip>
          </span>
          <b v-else>{{ eventData.itemId }}</b>
          {{ eventData.description.toLowerCase() }}
        </template>
      </div>
      <div class="flex items-center q-ml-auto">
        <span class="date flex items-center">
          <q-icon name="engp:engp-ico-clock" size="16px" class="q-mr-xs">
            <q-tooltip>Created at {{ $options.dayjs(eventData.createdAt).format('DD.MM.YYYY HH:mm') }}</q-tooltip>
          </q-icon>
        </span>
        <q-icon
          v-if="isChildProcessEvent"
          name="key"
          size="20px"
          class="key-link-icon cursor-pointer"
          color="primary"
          @click.stop="goToSco(eventProcessInstanceKey)"
        >
          <q-tooltip style="width: 220px">processInstanceKey: {{ eventProcessInstanceKey }}</q-tooltip>
        </q-icon>
        <q-icon
          name="engp:engp-ico-copy"
          @click.stop="copyUuid(eventData.uuid)"
          size="16px"
          class="cursor-pointer q-mx-md"
        >
          <q-tooltip>Copy UUID</q-tooltip>
        </q-icon>
      </div>
    </div>
  </div>
  <q-expansion-item
    v-else
    class="event bg-white btn--no-hover"
    :class="{
      'cursor-default': !(eventData?.subscriptions.length || eventData?.events.length),
      error: eventData.isError,
      'bg-grey-3': eventStatus.status === 'cancelled',
      expanded: expand,
      collapsed: !expand,
    }"
    hide-expand-icon
    v-model="expand"
  >
    <template v-slot:header>
      <div class="full-width flex items-start">
        <div class="flex items-center event-name q-mr-lg">
          <q-icon
            :name="eventData.type === 'diff' ? 'difference' : `engp:engp-ico-${eventData.type}`"
            size="16px"
            class="q-mr-xs"
          ></q-icon>
          <b>{{ eventData.type }}</b>
          <q-icon v-if="eventData.isError" name="cancel" class="q-ml-xs" color="negative" size="xs"></q-icon>
        </div>
        <Status v-if="eventData?.subscriptions.length" :showTooltip="false" :workflow="eventStatus" class="q-mr-md" />
        <div class="event-message">
          <div class="service-description-markdown full-width" v-html="dataMarkdown(eventData.message)"></div>
          <q-btn
            v-if="eventData.type === 'diff'"
            flat
            round
            dense
            text-color="blue"
            no-caps
            @click.stop="showDiff = true"
            >Show diff</q-btn
          >
        </div>

        <JsonDiffViewer
          v-if="eventData.type === 'diff'"
          v-model="showDiff"
          :before="eventData.before"
          :after="eventData.after"
        />

        <div class="flex items-center q-ml-auto">
          <span v-if="eventData.type === 'timer'">
            <q-circular-progress
              :value="progress"
              size="18px"
              :thickness="0.3"
              track-color="grey-4"
              color="primary"
              class="q-mr-md"
            >
            </q-circular-progress>
            <q-tooltip>Ends at {{ finishTime }}</q-tooltip>
          </span>

          <span class="date flex items-center">
            <q-icon name="engp:engp-ico-clock" size="16px" class="q-mr-xs">
              <q-tooltip>Created at {{ $options.dayjs(eventData.createdAt).format('DD.MM.YYYY HH:mm') }}</q-tooltip>
            </q-icon>
          </span>
          <q-icon
            name="engp:engp-ico-copy"
            @click.stop="copyUuid(eventData.uuid)"
            size="16px"
            class="cursor-pointer q-mx-md"
          >
            <q-tooltip>Copy UUID</q-tooltip>
          </q-icon>
          <q-icon
            v-if="isChildProcessEvent"
            name="key"
            size="20px"
            class="key-link-icon cursor-pointer"
            color="info"
            @click.stop="goToSco(eventProcessInstanceKey)"
          >
            <q-tooltip style="width: 220px">processInstanceKey: {{ eventProcessInstanceKey }}</q-tooltip>
          </q-icon>
          <q-icon
            v-if="eventData.type === 'subscription' || eventData.events.length"
            class="event-arrow"
            size="sm"
            name="keyboard_arrow_down"
          ></q-icon>
          <sup>
            <q-icon v-if="eventData?.hasChildError" name="cancel" class="q-mb-md" color="negative" size="12px"></q-icon>
          </sup>
        </div>
      </div>
    </template>
    <div v-if="eventData?.subscriptions.length || eventData?.events.length" class="q-mb-sm q-px-md q-pb-md">
      <div
        v-for="subscription in eventData?.subscriptions"
        class="event-item q-mb-xs q-px-lg q-py-lg bg-white"
        :class="{ 'bg-grey-2': subscription.status === 'cancelled' }"
        :key="subscription.uuid"
      >
        <div>
          <action
            :actionData="subscription"
            :workflowProcessInstanceKey="workflowProcessInstanceKey"
            :eventProcessInstanceKey="eventProcessInstanceKey"
            @resolve="reload()"
          />
        </div>
      </div>
      <div v-if="eventData?.events?.length">
        <event
          v-for="event in eventData.events"
          :key="event.uuid"
          class="child-event"
          :eventData="event"
          :workflowProcessInstanceKey="workflowProcessInstanceKey"
        ></event>
      </div>
    </div>
  </q-expansion-item>
</template>

<script>
import dayjs from 'dayjs';
import { marked } from 'marked';
import { copyToClipboard } from 'quasar';
import emitter from 'tiny-emitter/instance';
import Action from './Action.vue';
import Status from './Status.vue';
import JsonDiffViewer from './JsonDiffViewer.vue';
import { supportCntrlClick } from '@/shared/helpers';

const renderer = new marked.Renderer();

renderer.link = function (href, title, text) {
  return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
};

marked.setOptions({ renderer });

export default {
  dayjs,
  copyToClipboard,
  props: {
    eventData: {
      type: Object,
    },
    workflowProcessInstanceKey: {
      type: String,
      default: undefined,
    },
  },
  name: 'EventItem',
  emits: ['resolve', 'reload'],
  components: {
    Action,
    Status,
    JsonDiffViewer,
  },
  created() {
    emitter.on('expandCollapseAll', (flag) => {
      this.expand = flag;
    });
  },
  data() {
    return {
      expand: false,
      showDiff: false,
      progress: 5,
      progressInterval: null,
    };
  },
  mounted() {
    this.progressInterval = setInterval(this.updateProgress, 1000);
  },
  beforeUnmount() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  },
  computed: {
    finishTime() {
      const createdAtDate = new Date(this.eventData.createdAt);
      const finishTimestamp = createdAtDate.getTime() + this.eventData?.duration * 1000;
      const finishDate = new Date(finishTimestamp);
      return dayjs(finishDate).format('DD.MM.YYYY HH:mm');
    },
    eventStatus() {
      const eventStatus = {
        status: undefined,
      };
      if (this.eventData.subscriptions?.find((i) => i.cancelled)) {
        eventStatus.status = 'cancelled';
      }
      if (this.eventData.subscriptions?.find((i) => i.status === 'resolved')) {
        eventStatus.status = 'resolved';
      }
      if (this.eventData.subscriptions?.find((i) => i.status === 'pending')) {
        eventStatus.status = 'pending';
      }

      return eventStatus;
    },
    eventProcessInstanceKey() {
      return (
        this.eventData?.processInstanceKey ||
        this.eventData?.process_instance_key ||
        this.eventData?.processInstanceId ||
        this.eventData?.process_instance_id ||
        undefined
      );
    },
    isChildProcessEvent() {
      return Boolean(
        this.eventProcessInstanceKey &&
          this.workflowProcessInstanceKey &&
          this.eventProcessInstanceKey !== this.workflowProcessInstanceKey,
      );
    },
  },
  methods: {
    goToSco(id) {
      window.open(`${process.env.VUE_APP_OASIS_URL}/${id}`, '_blank');
    },
    goToItem(event, data) {
      let url = '';
      const { itemType, itemId } = data;
      if (itemType.includes('dns')) {
        url = `/dns-records?id=${itemId}`;
      } else if (itemType.includes('cloudente')) {
        url = `/cloudflare-zones?id=${itemId}`;
      } else if (itemType.includes('firewall_rule')) {
        url = `/firewall-rules?id=${itemId}`;
      } else if (itemType.includes('workflows')) {
        url = `/workflows/${itemId}`;
      } else if (itemType.includes('auth_schema')) {
        url = `/authorization-schemas?uuid=${itemId}`;
      } else if (itemType.includes('blueprint')) {
        url = `/blueprints?uuid=${itemId}`;
      } else if (itemType.includes('infra_service_config')) {
        url = `/infra-service-configs?uuid=${itemId}`;
      } else if (itemType.includes('service_schema')) {
        url = `/service-schemas?uuid=${itemId}`;
      } else {
        url = `/registration-environments?id=${itemId}`;
      }
      supportCntrlClick(event, url);
    },
    normilizeItem(data) {
      let item = '';
      if (data.includes('dns')) {
        item = 'DNS record';
      } else if (data.includes('cloudente')) {
        item = 'Cloudflare zone';
      } else if (data.includes('firewall_rule')) {
        item = 'Firewall Rule (EAR) request';
      } else if (data.includes('environments')) {
        item = 'Registration environment';
      } else if (data.includes('registration')) {
        item = 'Registration';
      } else if (data.includes('workflows')) {
        item = 'Workflow';
      } else if (data.includes('auth_schema')) {
        item = 'Authorization schema';
      } else if (data.includes('blueprint')) {
        item = 'Blueprint';
      } else if (data.includes('infra_service_config')) {
        item = 'Infraservice config';
      } else if (data.includes('service_schema')) {
        item = 'Service schema';
      } else {
        item = 'Item';
      }
      return item;
    },
    updateProgress() {
      const now = new Date();
      const elapsed = (now - new Date(this.eventData.createdAt)) / 1000;
      this.progress = Math.min((elapsed / this.eventData.duration) * 100, 100);
      if (this.progress >= 100) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },
    copyUuid(uuid) {
      copyToClipboard(uuid).then(() => {
        this.$notify('info', 'Info', 'Subscription UUID was copied');
      });
    },
    reload() {
      this.$emit('reload');
    },
    dataMarkdown(data) {
      return marked(data);
    },
  },
};
</script>

<style lang="scss">
.progress-container {
  width: 100%;
  max-width: 320px;
  text-align: center;
}

.progress-bar-background {
  width: 100%;
  height: 16px;
  background-color: #e0e0e0;
  border-radius: 10px;
}

.progress-bar-foreground {
  height: 100%;
  background-color: #73e1a5;
  border-radius: 10px;
}
.event.expanded > .q-expansion-item__container {
  .event-arrow {
    transform: rotate(180deg);
  }
}
.event.collapsed > .q-expansion-item__container {
  .event-arrow {
    transform: rotate(0deg);
  }
}
.event {
  border-radius: 15px;
  &.error {
    background-color: #ffe8e8 !important;
  }
  &.cursor-default {
    .q-item {
      cursor: default !important;
    }
  }
  .event-arrow {
    transition: all 0.5s;
  }

  .service-description-markdown {
    font-size: 14px;
  }

  .event-message,
  .timer-holder {
    max-width: 59%;
    width: 100%;
    word-break: break-word;

    @media (max-width: $breakpoint-md-max) {
      max-width: 100%;
      width: 100%;
      order: 4;
      margin-top: 10px;
      text-align: center;
      margin-left: 0;
    }
  }

  .q-item {
    padding: 24px;
  }

  .event-item,
  .child-event {
    border: 1px solid $grey-5;
    border-radius: 15px;
    margin-bottom: 8px;
  }
}

.event-name {
  min-width: 100px;
  text-transform: capitalize;
}

.relation {
  padding: 24px;
  border: 2px solid var(--q-primary);
  border-radius: 15px;
  border-style: dashed;
  &.related-item {
    border: 2px solid var(--q-info);
    border-style: dashed;
  }
}

.key-link-icon {
  cursor: pointer;
  transform: rotate(45deg);
  margin: 0 6px;
}
</style>
